import React, { useEffect, useState } from 'react';
import {
    Table, Button, Space, Tag, Modal, Descriptions, Select, message, Input, DatePicker,
    Rate, Tooltip, Spin,
    Form
} from 'antd';
import { SearchOutlined, DownloadOutlined, EditOutlined, CheckOutlined, UserDeleteOutlined } from '@ant-design/icons';
import { apiCall } from '../../API/RestApi';
import { useSelector } from 'react-redux';
import { dateFormat } from '../../Utils/formatDate';
import moment from 'moment';
import { exportLoanExcel } from './excel/LoanExcel';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;

const LoanApprovalList = () => {
    const navigate = useNavigate();
    const token = useSelector((state) => state.user.token);
    const [data, setData] = useState([]);
    const [meta, setMeta] = useState({ total: 0, page: 1, limit: 10 });
    const [loading, setLoading] = useState(false);
    const [selectedLoan, setSelectedLoan] = useState(null);
    const [detailVisible, setDetailVisible] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');
    const [approvalStatus, setApprovalStatus] = useState('');
    const [reason, setReason] = useState('');
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [bulkModalVisible, setBulkModalVisible] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [bulkStatus, setBulkStatus] = useState('');
    const [bulkReason, setBulkReason] = useState('');
    const [search, setSearch] = useState('');
    const [exportModalVisible, setExportModalVisible] = useState(false);
    const [exportRange, setExportRange] = useState([]);
    const [sorter, setSorter] = useState({ field: 'loan_request_id', order: 'descend' });
    const [imageLoading, setImageLoading] = useState(true);
    const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
    const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
    const [feedbackForm] = Form.useForm();
    // === FETCH DATA ===
    const fetchData = async (page = 1, limit = 10, sort = sorter) => {
        setLoading(true);
        try {
            const sortOrder = sort.order === 'ascend' ? 'asc' : 'desc';
            const res = await apiCall({
                method: 'GET',
                url: `loan-requests?page=${page}&limit=${limit}&search=${search}&sortBy=${sort.field}&order=${sortOrder}&approval_status=${statusFilter}`,
                data: {
                    headers: { Authorization: 'Bearer ' + token },
                },
            });
            if (res.status === 'success') {
                setData(res.data.data);
                setMeta({
                    page: res.data.page,
                    limit: res.data.limit,
                    total: res.data.total_data,
                });
            } else {
                message.error(res.message);
            }
        } catch {
            message.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            fetchData(1, meta.limit);
        }, 500);
        return () => clearTimeout(timeout);
    }, [search, statusFilter]);

    const openFeedbackModal = (record) => {
        setSelectedLoan(record);

        feedbackForm.setFieldsValue({
            satisfaction: record.loan_feedback?.satisfaction,
            rating: record.loan_feedback?.rating,
            comments: record.loan_feedback?.comments,
        });

        setFeedbackModalVisible(true);
    };

    const handleSubmitFeedback = async () => {
        try {
            const values = await feedbackForm.validateFields();

            setFeedbackSubmitting(true);

            const res = await apiCall({
                method: 'POST',
                url: '/loan-feedback',
                data: {
                    data: {
                        loan_request_id: selectedLoan.loan_request_id,
                        satisfaction: values.satisfaction,
                        rating: values.rating,
                        comments: values.comments || '',
                    },
                    headers: {
                        Authorization: 'Bearer ' + token,
                        'Content-Type': 'application/json',
                    },
                },
            });

            if (res.status === 'success') {
                message.success('Feedback submitted successfully');
                setFeedbackModalVisible(false);
                feedbackForm.resetFields();
                fetchData(meta.page, meta.limit);
            } else {
                message.error(res.message);
            }
        } catch (error) {
            if (error?.errorFields) return;

            message.error('Failed to submit feedback');
        } finally {
            setFeedbackSubmitting(false);
        }
    };

    // === CHANGE STATUS (SINGLE) ===
    const handleChangeStatus = async () => {
        if (!approvalStatus) return message.warning("Please select a status");

        try {
            const res = await apiCall({
                method: 'PUT',
                url: `/loan-requests/${selectedLoan.loan_request_id}/status`,
                data: {
                    data: {
                        status: approvalStatus,
                        reason: reason || '',
                    },
                    headers: {
                        Authorization: 'Bearer ' + token,
                        'Content-Type': 'application/json',
                    },
                },
            });

            if (res.status === 'success') {
                message.success('Status updated successfully');
                setEditModalVisible(false);
                setDetailVisible(false);
                fetchData(meta.page, meta.limit);
            } else {
                message.error(res.message);
            }
        } catch {
            message.error('Failed to change status');
        }
    };

    // === BULK APPROVAL ===
    const handleBulkApproval = async () => {
        if (selectedRowKeys.length === 0)
            return message.warning("Please select at least one record");
        if (!bulkStatus)
            return message.warning("Please select a status");

        try {
            const res = await apiCall({
                method: 'POST',
                url: `/loan-requests/approval`,
                data: {
                    data: {
                        ids: selectedRowKeys,
                        status_approval: bulkStatus,
                        reason: bulkReason || '',
                    },
                    headers: {
                        Authorization: 'Bearer ' + token,
                        'Content-Type': 'application/json',
                    },
                },
            });

            if (res.status === 'success') {
                message.success('Bulk status updated successfully');
                setBulkModalVisible(false);
                setSelectedRowKeys([]);
                fetchData(meta.page, meta.limit);
            } else {
                message.error(res.message);
            }
        } catch {
            message.error('Failed to update bulk approval');
        }
    };

    // === TABLE COLUMNS ===
    const columns = [
        {
            title: 'No',
            render: (_, __, index) => (meta.page - 1) * meta.limit + index + 1,
        },
        {
            title: 'Name',
            render: (_, record) => record.user_request?.name || '-',
        },
        {
            title: 'Vehicle',
            dataIndex: 'vehicle',
            render: (_, record) => record.vehicle
                ? record.vehicle.license_plate + " " + record.vehicle.brand
                : '-',
        },
        {
            title: 'Request Date',
            dataIndex: 'request_date',
            sorter: true,
            render: (_, record) => dateFormat(record.request_date),
        },
        {
            title: 'Purpose',
            dataIndex: 'purpose',
        },
        {
            title: 'Status',
            dataIndex: 'approval_status',
            sorter: true,
            render: (status) => (
                <Tag color={status === 'Approved' ? 'green' : status === 'Pending' ? 'orange' : 'red'}>
                    {status}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            render: (_, record) => (
                <Space>
                    <Button
                        size="small"
                        onClick={() => {
                            setSelectedLoan(record);
                            setImageLoading(true);
                            setDetailVisible(true);
                        }}
                    >
                        View
                    </Button>

                    <Tooltip title="Change status">
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => {
                                setSelectedLoan(record);
                                setApprovalStatus(record.approval_status || '');
                                setReason(record.reason || '');
                                setEditModalVisible(true);
                            }}
                        />
                    </Tooltip>

                    {/* {record.approval_status === 'Approved' && (
                        <Button
                            size="small"
                            type={record.loan_feedback ? 'default' : 'primary'}
                            onClick={() => openFeedbackModal(record)}
                        >
                            {record.loan_feedback ? 'Edit Feedback' : 'Feedback'}
                        </Button>
                    )} */}
                    {record.approval_status === 'Approved' && !record.loan_feedback && (
                        <Button
                            size="small"
                            type="primary"
                            onClick={() => openFeedbackModal(record)}
                        >
                            Feedback
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
        getCheckboxProps: (record) => ({
            disabled: record.approval_status !== 'Pending', // hanya Pending bisa dicentang
        }),
    };

    return (
        <div>
            <h2>Loan Approval</h2>

            <Space wrap align="start" style={{ width: '100%', justifyContent: 'space-between', marginBottom: 10 }}>
                <div>
                    <Input
                        placeholder="Search by user, vehicle, or purpose"
                        value={search}
                        prefix={<SearchOutlined />}
                        onChange={(e) => setSearch(e.target.value)}
                        onPressEnter={() => fetchData(1, meta.limit)}
                        allowClear
                        style={{ width: 300 }}
                    />

                    <Select
                        placeholder="Filter by status"
                        allowClear
                        value={statusFilter || undefined}
                        onChange={(value) => {
                            setStatusFilter(value || '');
                            if (!value) fetchData(1, meta.limit); // reload semua data saat filter di-clear
                        }}
                        style={{ width: 180, marginLeft: 10, marginRight: 10 }}
                    >
                        <Option value="Pending">Pending</Option>
                        <Option value="Approved">Approved</Option>
                        <Option value="Rejected">Rejected</Option>
                    </Select>
                    <Tooltip title="Ban user from vehicle loan">
                        <Button
                            type="primary"
                            danger
                            icon={<UserDeleteOutlined />}
                            onClick={() => navigate('/loanuserban')}
                        >
                            Ban User
                        </Button>
                    </Tooltip>
                </div>
                <Space>
                    <Tooltip title="Bulk approve/reject">
                        <Button
                            type="default"
                            icon={<CheckOutlined />}
                            onClick={() => setBulkModalVisible(true)}
                            disabled={selectedRowKeys.length === 0}
                        >
                            Bulk Action
                        </Button>
                    </Tooltip>
                    <Tooltip title="Download approval data in Excel">
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={() => setExportModalVisible(true)}
                        >
                            Export
                        </Button>
                    </Tooltip>
                </Space>
            </Space>

            <Table
                rowSelection={rowSelection}
                columns={columns}
                dataSource={data}
                rowKey="loan_request_id"
                loading={loading}
                pagination={{
                    current: meta.page,
                    pageSize: meta.limit,
                    total: meta.total,
                    showSizeChanger: true,
                }}
                onChange={(pagination, filters, sorterObj) => {
                    const sort = {
                        field: sorterObj.field || 'loan_request_id',
                        order: sorterObj.order || 'descend',
                    };
                    setSorter(sort);
                    fetchData(pagination.current, pagination.pageSize, sort);
                    setMeta(prev => ({ ...prev, page: pagination.current, limit: pagination.pageSize }));
                }}
            />

            {/* DETAIL MODAL */}
            <Modal
                title="Loan Request Detail"
                open={detailVisible}
                onCancel={() => setDetailVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setDetailVisible(false)}>Close</Button>,
                    <Button key="change" type="primary" onClick={() => setEditModalVisible(true)}>
                        Change Status
                    </Button>
                ]}
            >
                {selectedLoan && (
                    <Descriptions column={1} bordered size="small">
                        <Descriptions.Item label="Request Date">{dateFormat(selectedLoan.request_date)}</Descriptions.Item>
                        <Descriptions.Item label="License Plate">{selectedLoan.vehicle?.license_plate}</Descriptions.Item>
                        <Descriptions.Item label="Brand">{selectedLoan.vehicle?.brand}</Descriptions.Item>
                        <Descriptions.Item label="Model">{selectedLoan.vehicle?.model}</Descriptions.Item>
                        <Descriptions.Item label="Type">{selectedLoan.vehicle?.type}</Descriptions.Item>
                        <Descriptions.Item label="Start Date">{moment(selectedLoan.start_date).format('YYYY-MM-DD')}</Descriptions.Item>
                        <Descriptions.Item label="End Date">{moment(selectedLoan.end_date).format('YYYY-MM-DD')}</Descriptions.Item>
                        <Descriptions.Item label="Purpose">{selectedLoan.purpose}</Descriptions.Item>
                        <Descriptions.Item label="Requested By">{selectedLoan.user_request?.name} ({selectedLoan.user_request?.users_code})</Descriptions.Item>
                        <Descriptions.Item label="Position">{selectedLoan.user_request?.position?.name || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Photo">
                            {selectedLoan.user_request?.image ? (
                                <Spin spinning={imageLoading}>
                                    <img
                                        src={selectedLoan.user_request.image}
                                        alt="User"
                                        style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8 }}
                                        onLoad={() => setImageLoading(false)}
                                    />
                                </Spin>
                            ) : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Status">
                            <Tag color={
                                selectedLoan.approval_status === 'Approved' ? 'green' :
                                    selectedLoan.approval_status === 'Pending' ? 'orange' :
                                        'red'
                            }>
                                {selectedLoan.approval_status}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Reason">{selectedLoan.reason}</Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>

            {/* CHANGE STATUS MODAL */}
            <Modal
                title="Change Loan Request Status"
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                onOk={handleChangeStatus}
                okText="Submit"
                destroyOnClose
            >
                <Select
                    value={approvalStatus}
                    onChange={setApprovalStatus}
                    style={{ width: '100%', marginBottom: 16 }}
                    placeholder="Select new status"
                >
                    <Option value="Approved">Approved</Option>
                    <Option value="Pending">Pending</Option>
                    <Option value="Rejected">Rejected</Option>
                </Select>
                <Input.TextArea
                    rows={3}
                    placeholder="Reason (optional)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                />
            </Modal>

            {/* BULK MODAL */}
            <Modal
                title="Bulk Approval / Rejection"
                open={bulkModalVisible}
                onCancel={() => setBulkModalVisible(false)}
                onOk={handleBulkApproval}
                okText="Submit"
            >
                <p>{selectedRowKeys.length} item(s) selected</p>
                <Select
                    value={bulkStatus}
                    onChange={setBulkStatus}
                    style={{ width: '100%', marginBottom: 16 }}
                    placeholder="Select status"
                >
                    <Option value="Approved">Approved</Option>
                    <Option value="Rejected">Rejected</Option>
                </Select>
                <Input.TextArea
                    rows={3}
                    placeholder="Reason (optional)"
                    value={bulkReason}
                    onChange={(e) => setBulkReason(e.target.value)}
                />
            </Modal>

            {/* EXPORT MODAL */}
            <Modal
                title="Export Data"
                open={exportModalVisible}
                onCancel={() => setExportModalVisible(false)}
                onOk={() => exportLoanExcel(exportRange, token)}
                okText="Export"
            >
                <DatePicker.RangePicker
                    style={{ width: '100%' }}
                    value={exportRange}
                    onChange={setExportRange}
                />
            </Modal>

            <Modal
                title={selectedLoan?.loan_feedback ? 'Edit Loan Feedback' : 'Loan Feedback'}
                open={feedbackModalVisible}
                onCancel={() => {
                    setFeedbackModalVisible(false);
                    feedbackForm.resetFields();
                }}
                onOk={handleSubmitFeedback}
                okText="Submit"
                confirmLoading={feedbackSubmitting}
                destroyOnClose
            >
                {selectedLoan && (
                    <Descriptions
                        bordered
                        size="small"
                        column={1}
                        style={{ marginBottom: 20 }}
                    >
                        <Descriptions.Item label="Requested By">
                            {selectedLoan.user_request?.name || '-'}
                        </Descriptions.Item>

                        <Descriptions.Item label="Vehicle">
                            {selectedLoan.vehicle
                                ? `${selectedLoan.vehicle.license_plate} - ${selectedLoan.vehicle.brand} ${selectedLoan.vehicle.model}`
                                : '-'}
                        </Descriptions.Item>

                        <Descriptions.Item label="Purpose">
                            {selectedLoan.purpose || '-'}
                        </Descriptions.Item>
                    </Descriptions>
                )}

                <Form
                    form={feedbackForm}
                    layout="vertical"
                >
                    <Form.Item
                        name="satisfaction"
                        label="Satisfaction"
                        rules={[
                            {
                                required: true,
                                message: 'Please select satisfaction',
                            },
                        ]}
                    >
                        <Select placeholder="Select satisfaction">
                            <Option value="Satisfied">Satisfied</Option>
                            <Option value="Not Satisfied">Not Satisfied</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="rating"
                        label="Rating"
                        rules={[
                            {
                                required: true,
                                message: 'Please provide a rating',
                            },
                        ]}
                    >
                        <Rate />
                    </Form.Item>

                    <Form.Item
                        name="comments"
                        label="Comments"
                    >
                        <Input.TextArea
                            rows={4}
                            placeholder="Enter feedback comments"
                            maxLength={500}
                            showCount
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default LoanApprovalList;
